import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import { memoize } from 'lodash';
import * as React from 'react';
import { ReactNode } from 'react';
import { Params } from 'redux-little-router';
import { createSelector } from 'reselect';

import { Badge, BadgeTheme } from '../../components/Badge/Badge';
import { BetaMark } from '../../components/BetaMark/BetaMark';
import { DirectTooltipContent } from '../../components/DirectTooltipContent/DirectTooltipContent';
import { TooltipWrapper } from '../../components/TooltipWrapper/TooltipWrapper';
import { RoutesPath } from '../../routes/index';
import {
    CAMPAIGN_TYPES_ORDER,
    getCampaignCreateHref,
    getDescriptionForCampaignType,
    getIconNameForCampaignType,
    getTitleForCampaignType,
    isBetaCampaignType
} from '../../service/CampaignHelpers/CampaignHelpers';
import { HeaderHelpers } from '../../service/HeaderHelpers/HeaderHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { getLangSwitchInfo } from '../../service/LangSwitcher/LangSwitcher';
import { fireHeaderMetrika } from '../../service/Metrika/metrika';
import { UrlHelpers } from '../../service/UrlHelpers';
import {
    campaigns,
    DEALS_PREFIX,
    GRID_PREFIX,
    HeaderTabsData,
    LinkTarget,
    NavLinkData,
    Tld,
    UserAccountsInfo,
    WalletData
} from '../../typings/common';
import { DevInfo } from '../../typings/dev';
import { CampaignType } from '../../typings/domain';
import { GdMenuItem } from '../../typings/graphql-grid';
import { GridScreenUrlSearchQueryParams, GridScreenUrlSearchRouteParams } from '../../typings/grid';
import { MARKETERS_PREFIX } from '../../typings/marketers';
import { PRODUCTS_PREFIX } from '../../typings/products';
import { State } from '../../typings/state';

import * as keyset from './header.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

const getTitleFactory = memoize((isRecommendationTooltipHidden?: boolean) =>
    memoize(
        (onRecommendationTooltipClose: () => void): ReactNode => {
            return (
                <TooltipWrapper
                    size="m"
                    theme="white"
                    to={'bottom'}
                    visible={!isRecommendationTooltipHidden}
                    tooltipContent={
                        <DirectTooltipContent
                            header={i18n('recommendations-tooltip-header')}
                            content={i18n('recommendations-tooltip-content')}
                            hasClose={true}
                            onCloseClick={onRecommendationTooltipClose}
                        />
                    }
                >
                    {i18n('recommendations')}
                </TooltipWrapper>
            );
        }
    )
);

export const selectAvailableCampTypesTabs = createSelector(
    (state: State) => state.header.availableCampaignsTypes,
    (state: State) => state.commonData.login,
    (state: State) => state.commonData.operatorFeatures.isCampaignAddAllowed,
    (availableCampaignTypes: CampaignType[] = [], login: string, isCampaignAddAllowed: boolean) => {
        return CAMPAIGN_TYPES_ORDER.filter(type => availableCampaignTypes.includes(type)).map(type => ({
            key: type,
            title: isBetaCampaignType(type)
                ? [getTitleForCampaignType(type), ' ', <BetaMark key={type} />]
                : getTitleForCampaignType(type),
            icon: getIconNameForCampaignType(type),
            description: getDescriptionForCampaignType(type),
            location: getCampaignCreateHref(type, login, isCampaignAddAllowed),
            target: LinkTarget.blank,
            isExternal: true,
            isAvailable: true
        }));
    }
);

export const selectHeaderTabLinks = createSelector(
    selectAvailableCampTypesTabs,
    (state: State) => state.router.route,
    (state: State) => state.router.params,
    (state: State) => state.commonData.login,
    (state: State) => state.header.allowedMenuItems,
    (state: State) => state.commonData.operator.login !== state.commonData.client.login,
    (state: State) => state.commonData.fromOldInterface,
    (state: State) => state.header.canSeeDealsLinkEverywhere,
    (state: State) => state.header.dealsTotalCount,
    (state: State) => state.header.isAgency,
    (state: State) => state.commonData.isRecommendationTooltipHidden,
    (state: State) => state.commonData.client.ClientID,
    (
        availableCampTypesTabs: Array<HeaderTabsData>,
        route: string,
        params: Params,
        login: string,
        allowedMenuItems: GdMenuItem[],
        actsAsClient: boolean,
        fromOldInterface: boolean,
        canSeeDealsLinkEverywhere?: boolean,
        dealsTotalCount?: number,
        isAgency?: boolean,
        isRecommendationTooltipHidden?: boolean,
        clientId?: string
    ): Array<NavLinkData> => {
        const isShortGrid = route === RoutesPath.shortGrid;
        const isGrid = isShortGrid || route === RoutesPath.grid;
        const isRecommendations = route === RoutesPath.recommendationsPage;
        const isRecommendationsGrid = route === RoutesPath.recommendationsItems;
        const isLogs = route === RoutesPath.log;
        const isDeals = route === RoutesPath.deals;
        const isMinusPhraseLibrary = route === RoutesPath.minusPhrasesLibrary;
        const isInternalProducts = route === RoutesPath.products;
        const isInternalMarketers = route === RoutesPath.marketers;

        let showCampsLocation: string;

        if (fromOldInterface) {
            showCampsLocation = UrlHelpers.getOldCampaignListUrl(login);
        } else {
            showCampsLocation =
                isAgency || actsAsClient
                    ? UrlHelpers.createNewDirectLink(UrlHelpers.createNewDirectUrlPathByParams(GRID_PREFIX), {
                          [GridScreenUrlSearchQueryParams.ulogin]: login
                      })
                    : UrlHelpers.createNewDirectUrlPathByParams(GRID_PREFIX);
        }

        const hasMultipleSearchStrategies =
            allowedMenuItems !== undefined &&
            [GdMenuItem.SHOW_SEARCH_PAGE, GdMenuItem.INTERNAL_AD_SEARCH].every(key => allowedMenuItems.includes(key));

        const headerTabs: HeaderTabsData[] = [
            {
                key: GdMenuItem.SHOW_CAMPS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.SHOW_CAMPS]: true
                        }
                    });
                },
                title: actsAsClient ? i18n('campaigns-of-client') : i18n('my-campaigns'),
                isExternal: true,
                isAvailable: true,
                location: showCampsLocation,
                isActive: isShortGrid || (isGrid && params[GridScreenUrlSearchRouteParams.view] === campaigns)
            },
            {
                key: GdMenuItem.SHOW_AGENCY_CLIENTS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.SHOW_AGENCY_CLIENTS]: true
                        }
                    });
                },
                title: i18n('my-clients'),
                location: UrlHelpers.getDirectLink('showClients'),
                isExternal: true,
                isAvailable: true
            },
            {
                key: GdMenuItem.SHOW_MANAGER_CLIENTS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.SHOW_MANAGER_CLIENTS]: true
                        }
                    });
                },
                title: i18n('my-clients'),
                location: UrlHelpers.getDirectLink('showManagerMyClients'),
                isExternal: true,
                isAvailable: true
            },
            {
                key: GdMenuItem.INTERNAL_AD_SEARCH,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.INTERNAL_AD_SEARCH]: true
                        }
                    });
                },
                title: i18n('search'),
                location: UrlHelpers.getDirectLink('showInternalAdsSearchPage'),
                target: LinkTarget.blank,
                isExternal: true,
                isAvailable: !hasMultipleSearchStrategies
            },
            {
                key: GdMenuItem.CREATE,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.CREATE]: true
                        }
                    });
                },
                title: i18n('create'),
                location: UrlHelpers.getDirectLink('editCamp'),
                target: LinkTarget.blank,
                isExternal: true,
                isAvailable: true,
                group: availableCampTypesTabs
            },
            {
                key: GdMenuItem.INTERNAL_AD_MANAGE_PRODUCTS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.INTERNAL_AD_MANAGE_PRODUCTS]: true
                        }
                    });
                },
                title: i18n('products'),
                location: UrlHelpers.createNewDirectLink(UrlHelpers.createNewDirectUrlPathByParams(PRODUCTS_PREFIX), {
                    [GridScreenUrlSearchQueryParams.ulogin]: login
                }),
                isExternal: false,
                isAvailable: true,
                isActive: isInternalProducts
            },
            {
                key: GdMenuItem.INTERNAL_AD_MANAGE_MARKETERS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.INTERNAL_AD_MANAGE_MARKETERS]: true
                        }
                    });
                },
                title: i18n('marketers'),
                location: UrlHelpers.createNewDirectLink(UrlHelpers.createNewDirectUrlPathByParams(MARKETERS_PREFIX), {
                    [GridScreenUrlSearchQueryParams.ulogin]: login
                }),
                isExternal: false,
                isAvailable: true,
                isActive: isInternalMarketers
            },
            {
                key: '',
                title: i18n('tools'),
                group: [
                    {
                        key: GdMenuItem.AGENCY_SEARCH,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.AGENCY_SEARCH]: true
                                }
                            });
                        },
                        title: i18n('search'),
                        location: UrlHelpers.getDirectLink('agSearch'),
                        isExternal: true,
                        isAvailable: true
                    },
                    {
                        key: GdMenuItem.SHOW_SEARCH_PAGE,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.SHOW_SEARCH_PAGE]: true
                                }
                            });
                        },
                        title: i18n('search'),
                        target: LinkTarget.blank,
                        location: UrlHelpers.getDirectLink('showSearchPage'),
                        isExternal: true,
                        isAvailable: true
                    },
                    {
                        key: GdMenuItem.INTERNAL_AD_SEARCH,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.INTERNAL_AD_SEARCH]: true
                                }
                            });
                        },
                        title: i18n('internal-ads-search'),
                        location: UrlHelpers.getDirectLink('showInternalAdsSearchPage'),
                        target: LinkTarget.blank,
                        isExternal: true,
                        isAvailable: hasMultipleSearchStrategies
                    },
                    {
                        key: GdMenuItem.ADMIN,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.ADMIN]: true
                                }
                            });
                        },
                        title: i18n('administration'),
                        location: UrlHelpers.getDirectLink('adminPage'),
                        isExternal: true,
                        isAvailable: true
                    },
                    {
                        key: GdMenuItem.ADMIN_AGENCY,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.ADMIN_AGENCY]: true
                                }
                            });
                        },
                        title: i18n('administration'),
                        location: UrlHelpers.getDirectLink('adminPageAg'),
                        isExternal: true,
                        isAvailable: true
                    },
                    {
                        key: GdMenuItem.ADMIN_MANAGER,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.ADMIN_MANAGER]: true
                                }
                            });
                        },
                        title: i18n('administration'),
                        location: UrlHelpers.getDirectLink('adminPageMan'),
                        isExternal: true,
                        isAvailable: true
                    },
                    {
                        key: GdMenuItem.WORDSTAT,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.WORDSTAT]: true
                                }
                            });
                        },
                        title: i18n('word-stat'),
                        location: UrlHelpers.getWordStatUrl(),
                        target: LinkTarget.blank,
                        isExternal: true,
                        isAvailable: true,
                        description: i18n('word-stat-description')
                    },
                    {
                        key: GdMenuItem.ADVANCED_FORECAST,
                        onClick: (): void => {
                            fireHeaderMetrika({
                                click: {
                                    [GdMenuItem.ADVANCED_FORECAST]: true
                                }
                            });
                        },
                        title: i18n('budget-forecast'),
                        location: UrlHelpers.getDirectLink('advancedForecast'),
                        target: LinkTarget.blank,
                        isExternal: true,
                        isAvailable: true,
                        description: i18n('advanced-forecast-description')
                    }
                ]
            },
            {
                key: GdMenuItem.DEALS_LIST,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.DEALS_LIST]: true
                        }
                    });
                },
                title: i18nRaw('deals-list', {
                    count: <Badge theme={BadgeTheme.red}>{dealsTotalCount}</Badge>
                }) as string[],
                location: isAgency
                    ? UrlHelpers.createNewDirectUrlPathByParams(DEALS_PREFIX)
                    : UrlHelpers.getDealsViewLink(login),
                isExternal: true,
                isAvailable: dealsTotalCount !== undefined && canSeeDealsLinkEverywhere,
                isActive: isDeals
            },
            {
                key: GdMenuItem.RECOMMENDATIONS,
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.RECOMMENDATIONS]: true
                        }
                    });
                },
                location: UrlHelpers.getRecommendationsUrl(login),
                isExternal: true,
                isAvailable: isRecommendations || isGrid || isRecommendationsGrid || isLogs,
                isActive: isRecommendations,
                getTitle: getTitleFactory(isRecommendationTooltipHidden)
            },
            {
                key: GdMenuItem.SHOW_MINUS_KEYWORDS_LIB,
                title: i18n('library'),
                location: UrlHelpers.getMinusPhrasesLibraryUrl(login),
                isExternal: true,
                isAvailable: false,
                isActive: isMinusPhraseLibrary
            },
            {
                key: GdMenuItem.TURBO_LANDING_CONSTRUCTOR,
                title: i18n('turbo-landing-constructor'),
                onClick: (): void => {
                    fireHeaderMetrika({
                        click: {
                            [GdMenuItem.TURBO_LANDING_CONSTRUCTOR]: true
                        }
                    });
                },
                onMount: (): void => {
                    fireHeaderMetrika({
                        show: {
                            [GdMenuItem.TURBO_LANDING_CONSTRUCTOR]: true
                        }
                    });
                },
                location: UrlHelpers.getDirectLink('showTurboLandings', {
                    ulogin: login,
                    ClientID: clientId
                }),
                isExternal: true,
                isAvailable: Boolean(clientId)
            }
        ];

        return HeaderHelpers.getAllowedHeaderTabs(headerTabs, allowedMenuItems);
    }
);

export const selectInfoLinks = createSelector(
    (state: State) => state.commonData.tld,
    (tld: Tld) => {
        const currentLang = LangUtils.getCurrentLang();

        return HeaderHelpers.getInfoMenuLinks(tld, currentLang);
    }
);

export const selectHeaderWallet = createSelector(
    (state: State) => state.commonData.login,
    (state: State) => state.header.chiefUserId,
    (state: State) => state.header.wallet,
    (state: State) => state.header.agencyId,
    (login: string, chiefUserId: string, wallet?: WalletData, agencyId?: string) => {
        return {
            id: wallet ? wallet.id : undefined,
            url: UrlHelpers.getWalletUrl(login, agencyId ? agencyId : null),
            sum: wallet ? wallet.sum : undefined,
            currency: wallet ? wallet.currency : undefined
        };
    }
);

export const selectAdditionalUserInfo = createSelector(
    (state: State) => state.devInfo,
    (state: State) => state.header.userAccountsInfo,
    (devInfo: DevInfo, userAccountsInfo: UserAccountsInfo | undefined): string => {
        if (!devInfo.isBeta) {
            return '';
        }

        if (userAccountsInfo && userAccountsInfo.current) {
            return `UID: ${userAccountsInfo.current.uid}`;
        }

        return '';
    }
);

export const selectHeaderActionsMenu = createSelector(
    (state: State) => state.commonData.operator.tuneSecretKey,
    (state: State) => state.router.pathname,
    (state: State) => state.router.search,
    (tuneSecretKey: string, routerPathname: string, routerSearch: string) => {
        const path = UrlHelpers.generatePath(routerPathname, routerSearch);

        const { label, url } = getLangSwitchInfo(path, tuneSecretKey || '');

        return [
            {
                text: label,
                url,
                mix: []
            }
        ];
    }
);
